import { useEffect, useState } from "react";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { useAuth } from "../Authentification/AuthContext";
import { createAuthFetch } from "../Authentification/apiFetch";
import { TimeRange, getCutoffDate } from "./timeRange";

interface EvaluationResult {
  range: { from: string; to: string };
  rules: { key: string; label: string; description: string }[];
  compliance: Record<string, { percent: number | null; [key: string]: any }>;
  overallCompliancePercent: number | null;
  outcomes: {
    audience: { percent: number | null; detail: { totalValue: number; ausbildungStudiumPercent: number | null } };
    performance: { postCount: number; avgCtr: number | null; avgClicks: number | null; avgImpressions: number | null };
  };
}

interface MarketingConceptEvaluationProps {
  timeRange: TimeRange;
}

const complianceColor = (percent: number | null): string => {
  if (percent === null) return "#9ca3af";
  if (percent >= 75) return COLORS.success;
  if (percent >= 50) return COLORS.warning;
  return COLORS.danger;
};

const COLORS = {
  primary: "#008877",
  primaryLight: "#e6f4f2",

  success: "#008877",
  warning: "#b8872f",
  danger: "#b95858",

  muted: "#6b7280",
  track: "#edf1f3",
  surfaceMuted: "#f7f9fa",
};

function toIsoDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export default function MarketingConceptEvaluation({ timeRange }: MarketingConceptEvaluationProps) {
  const { token } = useAuth();
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const cutoff = getCutoffDate(timeRange);
    const from = cutoff ? toIsoDateOnly(cutoff) : "1970-01-01";
    const to = toIsoDateOnly(new Date());

    const apiFetch = createAuthFetch(token);
    apiFetch(`/api/marketing-evaluation?from=${from}&to=${to}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
        return res.json();
      })
      .then((data: EvaluationResult) => setResult(data))
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [timeRange, token]);

  return (
    <div>
      {isLoading && <p>Lade Auswertung...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {!isLoading && !error && result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <div style={{ width: 220, height: 140, position: "relative" }}>
              <ResponsiveContainer>
                <RadialBarChart
                  innerRadius="70%"
                  outerRadius="100%"
                  data={[{ value: result.overallCompliancePercent ?? 0 }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="value" fill={complianceColor(result.overallCompliancePercent)} cornerRadius={8} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div style={{ position: "absolute", top: "55%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700 }}>
                  {result.overallCompliancePercent !== null ? `${result.overallCompliancePercent.toFixed(0)}%` : "–"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Konzept-Einhaltung (beide Accounts)</div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {result.rules.map((rule) => {
              const data = result.compliance[rule.key];
              const percent = data?.percent ?? null;
              return (
                <div key={rule.key}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                    <span>
                      <strong>{rule.label}</strong>
                      <span style={{ color: "#6b7280" }}> — {rule.description}</span>
                    </span>
                    <span style={{ fontWeight: 600 }}>{percent !== null ? `${percent.toFixed(0)}%` : "keine Daten"}</span>
                  </div>
                  <div style={{ background: COLORS.track, borderRadius: 999, height: 8, overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${percent ?? 0}%`,
                        height: "100%",
                        background: complianceColor(percent),
                        borderRadius: 999,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 16 }}>
            <div style={{ flex: 1, padding: 16, background: "#f9fafb", borderRadius: 12 }}>
              <strong>Zielgruppe (Ergebnis)</strong>
              <div style={{ marginTop: 8, fontSize: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 24 }}>
                  {result.outcomes.audience.percent !== null ? `${result.outcomes.audience.percent.toFixed(0)}%` : "keine Daten"}
                </div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>
                  Anteil außerhalb Ausbildung/Studium
                  {result.outcomes.audience.detail.ausbildungStudiumPercent !== null &&
                    ` (${result.outcomes.audience.detail.ausbildungStudiumPercent.toFixed(1)}% Ausbildung/Studium)`}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, padding: 16, background: COLORS.surfaceMuted, borderRadius: 12 }}>
              <strong>Performance (Ziel: Traffic)</strong>
              <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 13 }}>
                <div>
                  <div style={{ color: "#6b7280", fontSize: 11 }}>Ø Impressions</div>
                  <div style={{ fontWeight: 600 }}>{result.outcomes.performance.avgImpressions?.toFixed(0) ?? "–"}</div>
                </div>
                <div>
                  <div style={{ color: "#6b7280", fontSize: 11 }}>Ø Klicks</div>
                  <div style={{ fontWeight: 600 }}>{result.outcomes.performance.avgClicks?.toFixed(1) ?? "–"}</div>
                </div>
                <div>
                  <div style={{ color: "#6b7280", fontSize: 11 }}>Ø CTR</div>
                  <div style={{ fontWeight: 600 }}>
                    {result.outcomes.performance.avgCtr !== null ? `${(result.outcomes.performance.avgCtr * 100).toFixed(1)}%` : "–"}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#6b7280", fontSize: 11 }}>Posts</div>
                  <div style={{ fontWeight: 600 }}>{result.outcomes.performance.postCount}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}